import { TableHeaderContentRenderer } from "@/components/basic";
import { useI18n } from "@/hooks/useI18n";
import { useVisits } from "@/hooks/useVisits";
import { dateParseISO } from "@/lib/utils";
import { IVisitor, StationVisitsFilter } from "@gettactic/api";
import { useAuthenticated } from "@gettactic/hooks";
import { Spoiler } from "@mantine/core";
import { format } from "date-fns";
import React, { useCallback, useMemo, useReducer, useState } from "react";
import { MdDelete, MdEdit } from "react-icons/md";
import { Column, Row } from "react-table";
import { useBreakpoint } from "@gettactic/helpers/src/hooks/useBreakpoint";

export type AdminVisitsFilterState = Omit<
  StationVisitsFilter,
  "start" | "end"
> & {
  start?: Date | null;
  end?: Date | null;
};
export function useAdminVisitsTable(
  type: "invitations" | "visits",
  officeId?: string | null
) {
  const [editVisit, setEditVisit] = useState<null | IVisitor>(null);
  const [deleteVisit, setDeleteVisit] = useState<null | IVisitor>(null);
  const {
    userContext: { currentOffice, authenticatedUser, deviceTz }
  } = useAuthenticated();
  const { templates } = useI18n(authenticatedUser);

  const defaultFilters = useMemo(
    () =>
      ({
        tz: deviceTz,
        office: authenticatedUser.offices?.offices.map((x) => x.id) ?? [],
        start: null,
        end: null,
        invited: false,
        uninvited: false,
        upcoming: type === "invitations",
        visits: type === "visits"
      }) as AdminVisitsFilterState,
    [currentOffice]
  );

  const [filter, updateFilter] = useReducer(
    (prev: AdminVisitsFilterState, next: Partial<AdminVisitsFilterState>) => {
      return { ...prev, ...next };
    },
    defaultFilters
  );

  const hasChangedFromDefaults = useMemo(() => {
    return Object.keys(filter).some((k) => {
      const key = k as keyof AdminVisitsFilterState;
      const d1 = filter[key];
      const d2 = defaultFilters[key];
      if (Array.isArray(d1) && Array.isArray(d2)) {
        return (
          d1.length !== d2.length ||
          d1.some((item, index: number) => !d2.includes(item))
        );
      }
      if (d1 instanceof Date && d2 instanceof Date) {
        return format(d1, "yyyy-MM-dd") !== format(d2, "yyyy-MM-dd");
      }
      return d1 !== d2;
    });
  }, [filter, defaultFilters]);

  const resetFilters = useCallback(() => {
    updateFilter(defaultFilters);
  }, [defaultFilters, updateFilter]);

  const commonParams: StationVisitsFilter = {
    ...filter,
    office: officeId ? [officeId] : filter.office,
    start: filter.start ? format(filter.start, "yyyy-MM-dd") : undefined,
    end: filter.end ? format(filter.end, "yyyy-MM-dd") : undefined,
    tz: deviceTz
  };

  const visits = useVisits(commonParams);
  const data = useMemo(
    () =>
      (visits.data?.pages || [])
        .map((x) => x?.elements ?? [])
        .reduce((arr, els) => {
          return arr.concat(els);
        }, []),
    [visits?.data]
  );

  const { isBreakpoint } = useBreakpoint();
  const isMd = isBreakpoint("md");

  const columns: Column<IVisitor>[] = React.useMemo(() => {
    const cols: Column<IVisitor>[] = [
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div className={isMd ? "pl-4" : undefined}>Host</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "host",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          return (
            <span className={isMd ? "pl-4" : undefined}>
              {row.original.host.name}
            </span>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Office</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "office",
        accessor: "office.name"
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Visitor Name</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "visitor-name",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          return (
            <Spoiler
              styles={{
                control: {
                  color: "primary",
                  textDecoration: "underline"
                }
              }}
              maxHeight={35}
              showLabel="Show more"
              hideLabel="Hide"
            >
              <div
                title={row.original.visitor.email}
                className="max-w-[210px] break-words"
              >
                {row.original.visitor.name}
              </div>
            </Spoiler>
          );
        }
      },
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Visitor Email</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "visitor-email",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          return (
            <Spoiler
              styles={{
                control: {
                  textDecoration: "underline"
                }
              }}
              maxHeight={35}
              showLabel="Show more"
              hideLabel="Hide"
            >
              <div
                title={row.original.visitor.email}
                className="max-w-[250px] break-words"
              >
                {row.original.visitor.email}
              </div>
            </Spoiler>
          );
        }
      },
      type === "invitations"
        ? {
            Header() {
              return (
                <TableHeaderContentRenderer>
                  <div>Expected Visit</div>
                </TableHeaderContentRenderer>
              );
            },
            id: "expected",
            accessor: undefined,
            Cell: function Cell({ row }: { row: Row<IVisitor> }) {
              const expected = row.original.visit.expected
                ? format(
                    dateParseISO(row.original.visit.expected),
                    templates.displayMonthDayYearWordy
                  )
                : "-";
              return <span>{expected}</span>;
            }
          }
        : null,
      type === "visits"
        ? {
            Header() {
              return (
                <TableHeaderContentRenderer>
                  <div>Station</div>
                </TableHeaderContentRenderer>
              );
            },
            id: "station",
            accessor: undefined,
            Cell: function Cell({ row }: { row: Row<IVisitor> }) {
              const station = row.original.station
                ? row.original.station.name
                : "-";
              return <span>{station}</span>;
            }
          }
        : null,
      type === "visits"
        ? {
            Header() {
              return (
                <TableHeaderContentRenderer>
                  <div>Type</div>
                </TableHeaderContentRenderer>
              );
            },
            id: "type",
            accessor: undefined,
            Cell: function Cell({ row }: { row: Row<IVisitor> }) {
              const type = row.original.visit.code ? "Invited" : "Walk-in";
              return <span>{type}</span>;
            }
          }
        : null,
      {
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Reason</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "reason",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          const reason = row.original.visit.reason
            ? row.original.visit.reason
            : "";
          const hasReason = reason.trim().length > 0;
          return (
            <div
              title={hasReason ? reason : "N/A"}
              className={hasReason ? "max-w-xs" : "pl-2"}
            >
              {hasReason ? (
                <Spoiler
                  styles={{
                    control: {
                      color: "primary"
                    }
                  }}
                  maxHeight={35}
                  showLabel="Show more"
                  hideLabel="Hide"
                >
                  {reason}
                </Spoiler>
              ) : (
                "-"
              )}
            </div>
          );
        }
      }
    ].filter((x) => x !== null) as Column<IVisitor>[];

    if (type === "visits") {
      cols.push({
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div>Checked-in</div>
            </TableHeaderContentRenderer>
          );
        },
        id: "presence",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          const presence = row.original.visit.presence
            ? format(
                dateParseISO(row.original.visit.presence),
                templates.displayMonthDayYearWordy + " h:mm a"
              )
            : "-";
          return <span>{presence}</span>;
        }
      });
    } else {
      cols.push({
        Header() {
          return (
            <TableHeaderContentRenderer>
              <div />
            </TableHeaderContentRenderer>
          );
        },
        id: "actions",
        accessor: undefined,
        Cell: function Cell({ row }: { row: Row<IVisitor> }) {
          return (
            <div className="flex flex-nowrap items-center p-4">
              <button
                onClick={() => setEditVisit(row.original)}
                className="px-2 text-gray-600 hover:text-gray-800"
              >
                <MdEdit className="h-5 w-5" />
              </button>
              <button
                onClick={() => setDeleteVisit(row.original)}
                className="px-2 text-gray-600 hover:text-gray-800"
              >
                <MdDelete className="h-5 w-5" />
              </button>
            </div>
          );
        }
      });
    }

    return cols;
  }, [type]);

  return {
    data,
    visits,
    columns,
    filter,
    hasChangedFromDefaults,
    updateFilter,
    resetFilters,
    setEditVisit,
    editVisit,
    setDeleteVisit,
    deleteVisit
  };
}
